import React from 'react';

import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { Button, Modal, notification } from 'antd';

import ComponentBlocker from '@src/components/ComponentBlocker';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { WORK_JOURNEY_STATUS } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import { useWorkJourneyValidationTranslations } from '@src/packages/workJourney/hooks/useWorkJourneyValidationTranslations';
import { notifyWorkJourneySaveError } from '@src/packages/workJourney/utils/submitWorkJourneyForm';
import WorkJourneyService from '@src/services/WorkJourneyService';
import safeExecute from '@src/util/safeExecute';

import styles from './WorkJourneyEditConfirm.module.less';

const workJourneyService = new WorkJourneyService();

const WorkJourneyApplyChangesConfirm = () => {
  const { workJourneyForm, setIsSaving, isSaving, orgId, modalVisibility, toggleModalVisibility } =
    useWorkJourneyContext();

  const history = useHistory();
  const intl = useIntl();
  const validationTranslations = useWorkJourneyValidationTranslations();

  const handleCancel = () => {
    toggleModalVisibility('applyChangesModal');
  };

  const applyChanges = async () => {
    if (isSaving) return;

    setIsSaving(true);
    safeExecute(async () => {
      const data = {
        ...workJourneyForm,
        state: WORK_JOURNEY_STATUS.PENDING,
      };

      const result = await workJourneyService
        .update(data, orgId, validationTranslations)
        .catch((error: unknown) => {
          notifyWorkJourneySaveError(error, intl, {
            members: data.members,
            matchUrl: `/${orgId}/-`,
          });
          return undefined;
        })
        .finally(() => {
          setIsSaving(false);
          toggleModalVisibility('applyChangesModal');
        });

      if (result) {
        const message = intl.formatMessage({ id: 'workJourney.updatedSuccessfully' });
        notification.success({ message });
        setTimeout(() => history.push(`/${orgId}/-/work-journey`), 500);
      }
    });
  };

  return (
    <Modal
      className={styles.modal}
      open={modalVisibility.applyChangesModal}
      onCancel={handleCancel}
      closable={false}
      title={null}
      width={520}
      footer={[
        <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
          <Button key="cancel" onClick={handleCancel} disabled={isSaving}>
            {intl.formatMessage({ id: 'button.cancel' })}
          </Button>
        </ComponentBlocker>,
        <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
          <Button
            key="apply"
            type="primary"
            disabled={isSaving}
            onClick={applyChanges}
            loading={isSaving}
          >
            {intl.formatMessage({ id: 'workJourney.edit.apply' })}
          </Button>
        </ComponentBlocker>,
      ]}
    >
      <div className={styles.iconContainer}>
        <span className={`material-icons-outlined ${styles.icon}`}>info</span>
      </div>

      <div className={styles.title}>
        {intl.formatMessage({ id: 'workJourney.edit.applyTitle' })}
      </div>

      <div className={styles.description}>
        {intl.formatMessage({ id: 'workJourney.edit.applyDescription' })}
      </div>
    </Modal>
  );
};

export default WorkJourneyApplyChangesConfirm;
